/**
 * Display logic for the bid form.
 *
 * Every rule here comes from `@bidtohire/shared`: this module only turns those
 * rules into the figures and sentences the interface shows while a company
 * types. A company may bid any whole-dollar amount from $5 up; publishing a
 * listing costs at least $5, and a raise is a top-up — the company pays the
 * difference between what it already holds and the total it wants to hold.
 *
 * The figures below are descriptive, not gates. Sitting above a row totalling
 * $T takes $T + $1, because a dollar is the smallest step there is; ties break
 * in favour of the earlier bid, so a dollar over a tied row clears the whole
 * tie block. No position costs a premium, so no string here may quote one.
 */

import {
  CENTS_PER_DOLLAR,
  MINIMUM_FIRST_BID_CENTS,
  claimRankForAmount,
  costToHoldRankCents,
  formatCents,
  isWholeDollars,
  minimumNextTotalCents,
  nextMinimumCents,
  topSpotTotalCents,
  validateBid,
} from '@bidtohire/shared';

/**
 * The price of publishing, rendered once and reused by every call to action
 * that invites a company to post. Posting is not free, so no button may imply
 * that it is, and no button may name a figure the payment code disagrees with.
 */
export const PUBLISH_FROM_LABEL = `from ${formatCents(MINIMUM_FIRST_BID_CENTS)}`;

export interface BidInputs {
  /** What this listing has paid over its lifetime, in cents. */
  readonly currentTotalCents: number;
  /** Every other listing's lifetime total in the scope being bid into. */
  readonly competingTotalsCents: readonly number[];
  /** The new lifetime total the company is proposing, in cents. */
  readonly desiredTotalCents: number | undefined;
}

export interface BidPreview {
  /** The smallest total this listing may hold after its next payment. */
  readonly minimumTotalCents: number;
  /** True once the typed amount is a legal bid. */
  readonly valid: boolean;
  /** Customer-facing reason the amount is not yet a legal bid. */
  readonly message: string | undefined;
  /** What Stripe would charge: the delta, never the whole total. */
  readonly chargeCents: number;
  /** The position the listing would hold at the proposed total. */
  readonly projectedRank: number;
  /** True when the proposed total lands at #1. */
  readonly takesFirstPlace: boolean;
  /** The total that would take first place in this scope. */
  readonly firstPlaceTotalCents: number;
}

/** The total that takes first place in a scope: the leader's total plus $1. */
export function firstPlaceTotalCents(competingTotalsCents: readonly number[]): number {
  return topSpotTotalCents(competingTotalsCents);
}

/**
 * The live preview beneath the amount field. An empty field is not an error —
 * it simply has nothing to preview yet.
 */
export function bidPreview(inputs: BidInputs): BidPreview {
  const { currentTotalCents, competingTotalsCents, desiredTotalCents } = inputs;
  const minimumTotalCents = minimumNextTotalCents(currentTotalCents);
  const firstPlace = firstPlaceTotalCents(competingTotalsCents);

  if (desiredTotalCents === undefined) {
    return {
      minimumTotalCents,
      valid: false,
      message: undefined,
      chargeCents: 0,
      projectedRank: claimRankForAmount(currentTotalCents, competingTotalsCents),
      takesFirstPlace: false,
      firstPlaceTotalCents: firstPlace,
    };
  }

  const projectedRank = claimRankForAmount(desiredTotalCents, competingTotalsCents);
  // One validator, so the preview refuses exactly what the server refuses and
  // accepts everything the server accepts — which is any whole-dollar total
  // clearing the opening or raise floor, wherever on the board it lands.
  const validation = validateBid(currentTotalCents, desiredTotalCents);

  if (!validation.ok) {
    return {
      minimumTotalCents,
      valid: false,
      message: validation.message,
      chargeCents: 0,
      projectedRank,
      takesFirstPlace: false,
      firstPlaceTotalCents: firstPlace,
    };
  }

  return {
    minimumTotalCents,
    valid: true,
    message: undefined,
    chargeCents: validation.chargeCents,
    projectedRank,
    // One function decides both, so the panel can never print "#1" and deny
    // that it is the top spot in the same sentence.
    takesFirstPlace: projectedRank === 1,
    firstPlaceTotalCents: firstPlace,
  };
}

/** A suggested bid the company can take in one click. */
export interface BidSuggestion {
  readonly label: string;
  readonly detail: string;
  readonly totalCents: number;
}

/**
 * The shortcuts offered beside the amount field: the opening or minimum raise,
 * the position just above where the listing sits now, and first place.
 */
export function bidSuggestions(inputs: Omit<BidInputs, 'desiredTotalCents'>): BidSuggestion[] {
  const { currentTotalCents, competingTotalsCents } = inputs;
  const minimum = minimumNextTotalCents(currentTotalCents);
  const currentRank = claimRankForAmount(currentTotalCents, competingTotalsCents);
  const first = Math.max(
    firstPlaceTotalCents(competingTotalsCents),
    minimumNextTotalCents(currentTotalCents),
  );
  const publishesAt = claimRankForAmount(minimum, competingTotalsCents);

  const suggestions: BidSuggestion[] = [
    {
      label: currentTotalCents === 0 ? 'Publish this listing' : 'Smallest raise',
      detail:
        currentTotalCents === 0
          ? competingTotalsCents.length === 0
            ? `First bids start at ${formatCents(MINIMUM_FIRST_BID_CENTS)}, and this board is empty.`
            : `First bids start at ${formatCents(MINIMUM_FIRST_BID_CENTS)}. This one publishes at #${publishesAt}.`
          : minimum >= first && currentRank > 1
            ? `Adds ${formatCents(minimum - currentTotalCents)} — the smallest raise here is the top spot.`
            : `Adds ${formatCents(minimum - currentTotalCents)} to your lifetime total.`,
      totalCents: minimum,
    },
  ];

  if (currentRank > 2) {
    const target = Math.max(
      costToHoldRankCents(competingTotalsCents, currentRank - 1),
      minimum,
    );
    // Ties above the target row mean a dollar over that row can vault the whole
    // tie block. When the figure lands at #1 the top-spot shortcut below already
    // offers it under a better name, so this one steps aside.
    const landedRank = claimRankForAmount(target, competingTotalsCents);
    if (landedRank > 1) {
      suggestions.push({
        label: `Move up to #${landedRank}`,
        detail: `Clears the listing at #${landedRank}.`,
        totalCents: target,
      });
    }
  }

  if (currentRank > 1) {
    suggestions.push({
      label: 'Take the top spot',
      detail: `A dollar over the leader's total.`,
      totalCents: first,
    });
  }

  // Never offer the same figure twice.
  const seen = new Set<number>();
  return suggestions.filter((suggestion) => {
    if (seen.has(suggestion.totalCents)) return false;
    seen.add(suggestion.totalCents);
    return true;
  });
}

/** What a row's position costs, and the rank that money actually buys. */
export interface ClaimOffer {
  /** The lifetime total a challenger must reach. */
  readonly priceCents: number;
  /** Where that total lands on this board — not necessarily the row's rank. */
  readonly rank: number;
}

/**
 * "Take this position for $1,201" — the figure that puts a challenger above a
 * given row, read against the whole board rather than the row alone.
 *
 * A dollar over a row clears every listing tied with it, so on a tied board
 * that dollar lands better than the row's own rank. The rank returned is where
 * the quoted figure genuinely lands, so no button can name a position its own
 * figure does not buy.
 */
export function priceToClaimRow(
  rowTotalCents: number,
  boardTotalsCents: readonly number[],
): ClaimOffer {
  const priceCents = nextMinimumCents(rowTotalCents);
  return { priceCents, rank: claimRankForAmount(priceCents, boardTotalsCents) };
}

export type DollarParse =
  | { readonly ok: true; readonly cents: number }
  | { readonly ok: false; readonly message: string };

/**
 * Read a typed amount. Bids are whole dollars, so `1,250` and `$1250` are the
 * same bid and `12.50` is not a bid at all.
 */
export function parseDollarInput(raw: string): DollarParse {
  const cleaned = raw.trim().replace(/^\$/, '').replace(/,/g, '');
  if (cleaned === '') return { ok: false, message: 'Enter an amount.' };
  if (!/^\d+$/.test(cleaned)) {
    return { ok: false, message: 'Bids are placed in whole dollars.' };
  }

  const dollars = Number(cleaned);
  const cents = dollars * CENTS_PER_DOLLAR;
  if (!Number.isSafeInteger(cents) || !isWholeDollars(cents)) {
    return { ok: false, message: 'That amount is larger than we can process.' };
  }
  return { ok: true, cents };
}

/** The amount field renders whole dollars without a currency symbol. */
export function dollarInputValue(cents: number): string {
  return String(Math.round(cents / CENTS_PER_DOLLAR));
}
